<template>
  <!-- BEGIN pos-counter-content -->
  <div class="mcd-shell">
    <!-- Header -->
    <header class="mcd-header">
      <div class="mcd-header-left">
        <div class="mcd-icon-badge">
          <i class="fa fa-id-card"></i>
        </div>
        <div>
          <h1 class="mcd-title">{{ $t('customerMemberCard.pageTitle') }}</h1>
          <p class="mcd-subtitle">{{ $t('customerMemberCard.directoryHint') }}</p>
        </div>
      </div>
    </header>

    <!-- Toolbar -->
    <div class="mcd-toolbar">
      <div class="mc-tabs">
        <button v-for="tab in statusTabs" :key="tab.value" type="button" class="mc-tab-btn"
          :class="{ 'mc-tab-btn-active': status_id === tab.value }" @click="selectStatus(tab.value)">
          {{ tab.label }}
          <span class="mc-tab-count" :class="{ 'mc-tab-count-alert': tab.value === 3 && status_id !== tab.value }">
            {{ tab.count }}
          </span>
        </button>
      </div>
      <div class="mcd-toolbar-right">
        <a-select v-model="combinedFilter" show-search option-filter-prop="children" style="width: 220px"
          placeholder="Promotion Package">
          <a-select-option value="all">{{ $t('customerMemberCard.allPackage') }}</a-select-option>
          <a-select-opt-group>
            <span slot="label">{{ $t('customerMemberCard.normal') }}</span>
            <a-select-option value="normal">{{ $t('customerMemberCard.normal') }}</a-select-option>
          </a-select-opt-group>
          <a-select-opt-group v-if="formViewData.promotions && formViewData.promotions.length">
            <span slot="label">{{ $t('customerMemberCard.package') }}</span>
            <a-select-option v-for="pkg in formViewData.promotions" :key="pkg.package_id"
              :value="'package-' + pkg.package_id">
              {{ pkg.package_name }}
            </a-select-option>
          </a-select-opt-group>
        </a-select>
        <div class="mcd-search">
          <i class="fa fa-search"></i>
          <input v-model="search" :placeholder="$t('customerMemberCard.search')" />
        </div>
      </div>
    </div>

    <!-- Grid -->
    <div class="mcd-body">
      <vue-custom-scrollbar class="mcd-scroll">
        <ts-loading-banner :loading="loading">
          <div class="mc-grid" v-if="filteredResources.length">
            <article v-for="row in filteredResources" :key="row.customer_member_card_id" class="mc-tile"
              :class="'mc-tile-' + statusClass(row.status_id)">
              <i class="fa fa-spa mc-tile-watermark"></i>
              <div class="mc-tile-body">
                <div class="mc-tile-top">
                  <span class="mc-serial-chip">
                    <i class="fa fa-credit-card"></i>
                    <span class="mc-serial">{{ row.serial_card }}</span>
                    <button type="button" class="mc-copy-btn" :title="$t('copy')"
                      @click.prevent="copySerial(row.serial_card)">
                      <i class="far fa-copy"></i>
                    </button>
                  </span>
                  <span class="mc-badge" :class="'mc-badge-' + statusClass(row.status_id)">
                    <span class="mc-badge-dot"></span>{{ row.status_name }}
                  </span>
                </div>

                <!-- Customer info -->
                <div class="mc-customer" v-if="row.customer_name">
                  <div class="mc-avatar">
                    {{ initials(row.customer_name) }}
                  </div>
                  <div class="mc-customer-info">
                    <h4>{{ row.customer_name }}</h4>
                    <p v-if="row.contact_number"><i class="fa fa-phone"></i>{{ row.contact_number }}</p>
                  </div>
                  <span class="mc-pos-tag" v-if="posLabel">{{ posLabel }}</span>
                </div>
                <div class="mc-unassigned" v-else>
                  <div class="mc-unassigned-title">
                    <i class="fa fa-user-plus"></i>{{ $t('customerMemberCard.unassignedCard') }}
                  </div>
                  <p>{{ $t('customerMemberCard.assignCustomerHint') }}</p>
                </div>

                <!-- Package / quota -->
                <div class="mc-quota">
                  <span class="mc-quota-label">
                    <span class="mc-quota-dot"></span>
                    {{ row.promotion_type_id === 2 ? (row.package_name || '-') : (row.card_types || '-') }}
                  </span>
                  <span class="mc-quota-value"
                    :class="{ 'mc-quota-value-full': (row.used_times || 0) >= row.max_times }">
                    {{ row.used_times || 0 }}/{{ row.max_times }}
                  </span>
                </div>
              </div>

              <div class="mc-tile-footer">
                <ts-button v-if="row.status_id === 3" color="success" size="sm"
                  :waiting="loadingRowId === row.customer_member_card_id" @click.prevent="assignCustomer(row)">
                  <i class="fa fa-user-plus fa-fw"></i>
                  {{ $t('customerMemberCard.assignCustomer') }}
                </ts-button>
                <ts-button v-else-if="row.customer_id && !row.ref_stock_out_id" color="warning" size="sm"
                  :waiting="loadingRowId === row.customer_member_card_id" @click.prevent="generateInvoice(row)">
                  <i class="fas fa-file-invoice-dollar fa-fw"></i>
                  {{ $t('customerMemberCard.generateInvoice') }}
                </ts-button>
                <template v-else-if="row.ref_stock_out_id">
                  <ts-button color="danger" size="sm" :waiting="loadingRowId === row.customer_member_card_id"
                    @click.prevent="viewInvoice(row)">
                    <i class="fas fa-file-pdf fa-fw"></i>
                    {{ $t('pdf') }}
                  </ts-button>
                  <ts-button color="primary" size="sm" :waiting="loadingRowId === row.customer_member_card_id"
                    @click.prevent="reprintInvoice(row)">
                    <i class="fa fa-print fa-fw"></i>
                    {{ $t('restaurantSale.reprint') }}
                  </ts-button>
                </template>
              </div>
            </article>
          </div>
          <div class="mc-empty" v-else>
            {{ $t('noData') }}
          </div>
        </ts-loading-banner>
      </vue-custom-scrollbar>
    </div>

    <!-- Footer -->
    <footer class="mcd-footer">
      <ts-button @click.prevent="$emit('cancel')"><i class="fa fa-arrow-left fa-fw fa-lg"></i>
        {{ $t('restaurantSale.back') }}</ts-button>
    </footer>

    <!-- assign customer / generate invoice / check bill -->
    <a-modal v-if="showAssign" v-model="showAssign" :closable="false" centered @cancel="closeAssign" :zIndex="1060"
      :footer="null" :body-style="{ padding: 0 }" :width="modalWidth" :maskClosable="false">
      <assign-customer v-if="assignStage === 'assign'" @cancel="closeAssign" @assigned="onCustomerAssigned" />
      <generate-invoice v-else-if="assignStage === 'invoice'" @cancel="closeAssign" @assigned="onAssigned" />
      <check-bill v-else-if="assignStage === 'success'" :data="successData" @close="closeAssign" />
    </a-modal>

    <!-- invoice preview -->
    <pdf-preview v-model="pdf_view" :invoice-id="invoiceId" @cancel="value => (pdf_view = value)" />
  </div>
  <!-- END pos-counter-content -->
</template>

<script>
import { mapActions, mapState } from 'vuex'
import vueCustomScrollbar from 'vue-custom-scrollbar'
import 'vue-custom-scrollbar/dist/vueScrollbar.css'
import AssignCustomer from '../memeber-card-sale/assign-customer.vue'
import GenerateInvoice from '../memeber-card-sale/generate-invoice.vue'
import CheckBill from '../memeber-card-sale/check-bill.vue'
import PdfPreview from '../../customer/customer-member-card/pdf-preview.vue'

export default {
  name: 'memberContent',
  components: {
    vueCustomScrollbar,
    AssignCustomer,
    GenerateInvoice,
    CheckBill,
    PdfPreview
  },
  data() {
    return {
      loading: false,
      search: '',
      status_id: 3,
      promotion_type_id: 0,
      package_id: null,
      showAssign: false,
      assignStage: null,
      successData: null,
      pdf_view: false,
      invoiceId: null,
      loadingRowId: null
    }
  },
  created() {
    this.fetchData()
    this.getFormViewData({ params: undefined, index: undefined })
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['resources', 'formViewData', 'edit_data']),
    statusTabs() {
      return [
        { label: 'All', value: 0, count: this.resources.length },
        { label: 'Active', value: 1, count: this.resources.filter(row => row.status_id === 1).length },
        { label: 'Pending', value: 3, count: this.resources.filter(row => row.status_id === 3).length },
        { label: 'Inactive', value: 2, count: this.resources.filter(row => row.status_id === 2).length }
      ]
    },
    filteredResources() {
      let rows = this.resources
      if (this.status_id !== 0) {
        rows = rows.filter(row => row.status_id === this.status_id)
      }
      const search = this.search.trim().toLowerCase()
      if (search) {
        rows = rows.filter(row =>
          (row.customer_name || '').toLowerCase().includes(search) ||
          (row.serial_card || '').toLowerCase().includes(search) ||
          (row.contact_number || '').toLowerCase().includes(search)
        )
      }
      return rows
    },
    combinedFilter: {
      get() {
        if (this.promotion_type_id === 2 && this.package_id) {
          return 'package-' + this.package_id
        }
        if (this.promotion_type_id === 1) {
          return 'normal'
        }
        return 'all'
      },
      set(value) {
        if (value === 'normal') {
          this.promotion_type_id = 1
          this.package_id = null
        } else if (typeof value === 'string' && value.indexOf('package-') === 0) {
          this.promotion_type_id = 2
          this.package_id = parseInt(value.replace('package-', ''), 10)
        } else {
          this.promotion_type_id = 0
          this.package_id = null
        }
        this.$store.commit('customer/customerMemberCard/RESET_CURRENT_PAGE')
        this.fetchData()
      }
    },
    modalWidth() {
      return this.assignStage === 'success' ? '480px' : '620px'
    },
    posLabel() {
      const counterId = localStorage.getItem('counterId')
      return counterId ? 'POS #' + String(counterId).padStart(2, '0') : null
    }
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    fetchData(attributes) {
      // Status filtering (All/Active/Pending/Inactive) happens client-side in
      // `filteredResources` against this one full fetch, so switching tabs is
      // instant instead of round-tripping to the server every click.
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/fetch', {
          status_id: 0,
          promotion_type_id: this.promotion_type_id,
          package_id: this.package_id,
          perPage: 9999,
          ...attributes
        })
        .finally(() => {
          this.loading = false
        })
    },
    statusClass(status_id) {
      if (status_id === 1) return 'active'
      if (status_id === 2) return 'inactive'
      if (status_id === 3) return 'pending'
      return 'inactive'
    },
    selectStatus(value) {
      this.status_id = value
    },
    // Called by the parent counter-checkout screen when a card is scanned
    // while this modal is open — surface the match immediately instead of
    // making the cashier switch tabs/type it in themselves.
    applyScannedSerial(serial) {
      this.status_id = 0
      this.search = serial
    },
    initials(name) {
      if (!name) return '-'
      const parts = name.trim().split(/\s+/)
      return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].substring(0, 2).toUpperCase()
    },
    copySerial(serial) {
      if (!serial || !navigator.clipboard) return
      navigator.clipboard.writeText(serial).then(() => {
        this.$toasted.success(this.$t('copied'))
      })
    },
    assignCustomer(record) {
      this.loadingRowId = record.customer_member_card_id
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.assignStage = 'assign'
      this.showAssign = true
      this.$nextTick(() => {
        this.loadingRowId = null
      })
    },
    generateInvoice(record) {
      this.loadingRowId = record.customer_member_card_id
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.assignStage = 'invoice'
      this.showAssign = true
      this.$nextTick(() => {
        this.loadingRowId = null
      })
    },
    onCustomerAssigned(payload) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {
        ...this.edit_data,
        customer_id: payload.customer_id,
        customer_name: payload.customer_name,
        contact_number: payload.contact_number
      })
      this.assignStage = 'invoice'
    },
    closeAssign() {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {})
      this.assignStage = null
      this.successData = null
      this.showAssign = false
    },
    onAssigned(payload) {
      this.successData = payload
      this.assignStage = 'success'
      this.fetchData()
    },
    viewInvoice(record) {
      this.loadingRowId = record.customer_member_card_id
      this.invoiceId = record.ref_stock_out_id
      this.pdf_view = true
      this.$nextTick(() => {
        this.loadingRowId = null
      })
    },
    reprintInvoice(record) {
      this.loadingRowId = record.customer_member_card_id
      this.$store
        .dispatch('restaurantSale/counterCheckout/printBill', {
          stock_out_id: record.ref_stock_out_id,
          counter_id: localStorage.getItem('counterId'),
          action_type: 'check-bill'
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loadingRowId = null
        })
    }
  },
  beforeDestroy() {
    this.$store.commit('customer/customerMemberCard/RESET_STATE')
  }
}
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500;600;700&display=swap');

.mcd-shell {
  display: flex;
  flex-direction: column;
  // .ant-modal-body has a fixed 24px padding on all sides — subtract it so
  // this shell fills exactly the space the full-screen modal gives it,
  // instead of relying on percentage-height through the antd flex chain.
  height: calc(100vh - 48px);
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.mcd-header {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.mcd-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mcd-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #eff6ff;
  color: #0057c0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.mcd-title {
  font-family: 'Manrope', sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.01em;
}

.mcd-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0;
}

/* Toolbar */
.mcd-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 4px;
}

.mcd-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.mcd-search {
  position: relative;
  width: 300px;
}

.mcd-search i {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 10px;
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 11px;
  pointer-events: none;
}

.mcd-search input {
  width: 100%;
  padding: 7px 10px 7px 28px;
  font-size: 12px;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.mcd-search input:focus {
  outline: none;
  background: #fff;
  border-color: #0057c0;
  box-shadow: 0 0 0 3px rgba(0, 87, 192, 0.12);
}

/* Status tabs */
.mc-tabs {
  display: inline-flex;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
  gap: 2px;
}

.mc-tab-btn {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.mc-tab-btn:hover {
  color: #0f172a;
}

.mc-tab-btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(0, 87, 192, 0.35);
}

.mc-tab-btn-active {
  background: #fff;
  color: #0057c0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.mc-tab-count {
  margin-left: 4px;
  background: #eff6ff;
  color: #0057c0;
  border-radius: 9999px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
}

.mc-tab-count-alert {
  background: #fee2e2;
  color: #dc2626;
}

/* Grid area */
.mcd-body {
  flex: 1;
  min-height: 0;
}

.mcd-scroll {
  height: 100%;
}

.mc-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  padding: 4px 4px 16px;

  @media (max-width: 90em) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 62em) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 37.5em) {
    grid-template-columns: 1fr;
  }
}

.mc-empty {
  text-align: center;
  color: #9ca3af;
  padding: 60px 0;
}

.mc-tile {
  position: relative;
  border: none;
  border-radius: 16px;
  color: #fff;
  background: radial-gradient(circle at 85% 10%, #c8935a 0%, #a8733f 45%, #855628 100%);
  box-shadow: 0 6px 18px rgba(133, 86, 40, 0.25);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  transition: all 0.15s ease;
}

.mc-tile:hover {
  box-shadow: 0 10px 25px -5px rgba(133, 86, 40, 0.4);
}

.mc-tile-inactive {
  opacity: 0.7;
}

.mc-tile-watermark {
  position: absolute;
  right: -12px;
  bottom: -16px;
  font-size: 110px;
  color: rgba(255, 255, 255, 0.12);
  pointer-events: none;
}

.mc-tile-body {
  position: relative;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mc-tile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mc-serial-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 11px;
}

.mc-serial {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mc-copy-btn {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  padding: 2px;
  cursor: pointer;
  flex-shrink: 0;
  outline: none;
}

.mc-copy-btn:hover {
  color: #fff;
}

.mc-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  white-space: nowrap;
}

.mc-badge-dot {
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background: currentColor;
}

.mc-badge-pending {
  background: #fef3c7;
  color: #b45309;
}

.mc-badge-active {
  background: #dcfce7;
  color: #15803d;
}

.mc-badge-inactive {
  background: #fee2e2;
  color: #b91c1c;
}

/* Customer info */
.mc-customer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.mc-avatar {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: #fff;
  color: #855628;
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mc-customer-info {
  min-width: 0;
  flex: 1;
}

.mc-customer-info h4 {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mc-customer-info p {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 11px;
  color: #fbe8cf;
  margin: 2px 0 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.mc-customer-info p i {
  font-size: 9px;
  color: #fbe8cf;
}

.mc-pos-tag {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 2px 8px;
  border-radius: 6px;
  flex-shrink: 0;
  white-space: nowrap;
}

.mc-unassigned {
  padding: 10px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px dashed rgba(255, 255, 255, 0.35);
  text-align: center;
}

.mc-unassigned-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

.mc-unassigned p {
  font-size: 10px;
  color: #fbe8cf;
  margin: 2px 0 0;
}

/* Quota row */
.mc-quota {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
}

.mc-quota-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #fff;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mc-quota-dot {
  width: 7px;
  height: 7px;
  border-radius: 3px;
  background: #fde6c4;
  flex-shrink: 0;
}

.mc-quota-value {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 700;
  color: #bbf7d0;
  flex-shrink: 0;
}

.mc-quota-value-full {
  color: rgba(255, 255, 255, 0.55);
}

.mc-tile-footer {
  position: relative;
  padding: 0 14px 14px;
  background: transparent;
  border-top: none;
  display: flex;
  gap: 8px;
}

.mc-tile-footer .btn {
  flex: 1 1 0;
  min-width: 0;
  border-radius: 8px;
}

.mc-tile-footer .btn-danger {
  background: #7f1d1d;
  border-color: #7f1d1d;
  color: #fff;
}

.mc-tile-footer .btn-danger:hover {
  background: #6b1818;
  border-color: #6b1818;
}

.mc-tile-footer .btn-primary {
  background: #fff8ee;
  border-color: #fff8ee;
  color: #7a4b1f;
}

.mc-tile-footer .btn-primary:hover {
  background: #f6e9d6;
  border-color: #f6e9d6;
}

/* Footer */
.mcd-footer {
  padding: 12px 4px 4px;
  border-top: 1px solid #e2e8f0;
  text-align: right;
}
</style>
